/** Billing panel.
 *
 * Flat per-company pricing (2026-06-27): a flat monthly base fee +
 * per-conference + 3-level signatures. Seats are gone — every plan
 * bundles unlimited operators, so there's no seat-count picker.
 *
 * The plan switcher passes `annual_billing` to the subscribe endpoint.
 * For paid plans the backend returns a Stripe Checkout URL (line items:
 * flat base licensed + per-axis metered SKUs); for the free plan it
 * flips the local FK directly.
 *
 * Customer Portal button opens Stripe-hosted UI for payment method /
 * invoices / cancellation. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { Pagination } from "@/components/Pagination";
import { PermissionGate } from "@/components/PermissionGate";
import type { Paginated } from "@/lib/types";

const INVOICES_PAGE_SIZE = 30;

import { DEFAULT_PACK, type PackFields } from "./packFields";
import { SignaturePackFields } from "./SignaturePackFields";
import type {
  BillingInvoice,
  BillingMeResponse,
  Plan,
  SignaturePackOption,
  SignaturePackRequest,
} from "@/lib/types";


export function BillingPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [annualBilling, setAnnualBilling] = useState(false);

  const me = useQuery({
    queryKey: ["billing-me"],
    queryFn: async () => (await api.get<BillingMeResponse>(ApiPaths.billingMe)).data,
  });
  const plans = useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => (await api.get<Plan[]>(ApiPaths.billingPlans)).data,
  });

  const subscribe = useMutation({
    mutationFn: async (slug: string) => {
      const res = await api.post<{ checkout_url?: string }>(
        ApiPaths.billingSubscribe,
        {
          plan_slug: slug,
          annual_billing: annualBilling,
        },
      );
      return res.data;
    },
    onSuccess: (data) => {
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      void qc.invalidateQueries({ queryKey: ["billing-me"] });
    },
  });

  const openPortal = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ portal_url: string }>(
        ApiPaths.billingPortal,
      );
      return res.data;
    },
    onSuccess: (data) => {
      window.open(data.portal_url, "_blank", "noopener,noreferrer");
    },
  });

  // Invoices (Phase 3-invoicing). Empty for subscription-engine orgs, so
  // the section only shows once an org is invoice-billed.
  const [invoicePage, setInvoicePage] = useState(1);
  const invoices = useQuery({
    queryKey: ["billing-invoices", invoicePage],
    queryFn: async () =>
      (
        await api.get<Paginated<BillingInvoice>>(ApiPaths.billingInvoices, {
          params: {
            page: String(invoicePage),
            page_size: String(INVOICES_PAGE_SIZE),
          },
        })
      ).data,
    placeholderData: (prev) => prev,
  });
  const invoiceRows = invoices.data?.results ?? [];
  const invoiceTotal = invoices.data?.count ?? 0;

  // Open an invoice PDF. Stripe-hosted invoices carry a public `pdf_url`
  // (open directly); external / wholesale-client invoices have none, so we
  // fetch the locally-rendered PDF through the authenticated API client (a
  // plain link would drop the Bearer token) and open the blob.
  const openInvoicePdf = async (inv: BillingInvoice) => {
    if (inv.pdf_url) {
      window.open(inv.pdf_url, "_blank", "noopener,noreferrer");
      return;
    }
    const res = await api.get(ApiPaths.billingInvoicePdf(inv.id), {
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Pre-paid signature packs (one-time Stripe Checkout). Catalog is
  // server-driven so prices stay single-source.
  const packs = useQuery({
    queryKey: ["billing-signature-packs"],
    queryFn: async () =>
      (
        await api.get<{ packs: SignaturePackOption[] }>(
          ApiPaths.billingSignaturePack,
        )
      ).data.packs,
  });
  const buyPack = useMutation({
    mutationFn: async (packKey: string) =>
      (
        await api.post<{ checkout_url?: string }>(
          ApiPaths.billingSignaturePack,
          { pack_key: packKey },
        )
      ).data,
    onSuccess: (data) => {
      if (data?.checkout_url) window.location.href = data.checkout_url;
    },
  });

  // Managed orgs can't self-checkout — they REQUEST a pack from their
  // reseller, who grants it. Show the catalog as request buttons + the
  // status of their recent requests.
  const packRequests = useQuery({
    queryKey: ["signature-pack-requests"],
    queryFn: async () =>
      (
        await api.get<SignaturePackRequest[]>(
          ApiPaths.billingSignaturePackRequest,
        )
      ).data,
    enabled: !!me.data?.usage?.is_managed,
  });
  const requestPack = useMutation({
    mutationFn: async (v: PackFields) =>
      (await api.post(ApiPaths.billingSignaturePackRequest, v)).data,
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["signature-pack-requests"] }),
  });
  const [packForm, setPackForm] = useState<PackFields>(DEFAULT_PACK);

  // Managed (reseller/commercial) accounts can't self-serve switch — they
  // raise a request that ops / their account manager acts on (§9a).
  const requestPlanChange = useMutation({
    mutationFn: async (message: string) => {
      await api.post(ApiPaths.billingPlanChangeRequest, { message });
    },
  });

  if (me.isLoading || plans.isLoading) {
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.billing.loading")}
      </section>
    );
  }
  if (!me.data || !plans.data) return null;

  const sub = me.data.subscription;
  const usage = me.data.usage;
  const currentPlan = sub.plan;
  const currentSlug = currentPlan.slug;

  return (
    <div className="space-y-4">
      {/* ---------- Current plan + usage ---------- */}
      <section className="card space-y-3">
        <header className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
              {t("admin.billing.currentPlan")}
            </h2>
            <p className="text-xl font-semibold text-nb-text">
              {currentPlan.name}
              {sub.annual_billing && (
                <span className="ml-2 text-xs text-nb-mint">
                  {t("admin.billing.annualBadge")}
                </span>
              )}
            </p>
            <PlanPricingLine plan={currentPlan} />
          </div>
          <span className="pill bg-slate-700/40 text-nb-text text-xs">
            {sub.status}
          </span>
        </header>

        <UsageGrid usage={usage} />

        {/* Managed (reseller/commercial) accounts are billed through their
            partner, not self-serve Stripe — there's no Customer Portal to
            open (no Stripe customer on file). Show a note instead of a
            button that can only ever error. */}
        {usage.is_managed ? (
          <p className="pt-2 text-xs text-nb-text-3">
            {t("admin.billing.managedBillingNote")}
          </p>
        ) : (
          <PermissionGate perm="billing.update">
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => openPortal.mutate()}
                disabled={openPortal.isPending}
                title={t("admin.billing.manageTooltip")}
              >
                {openPortal.isPending
                  ? t("admin.billing.managing")
                  : t("admin.billing.manageButton")}
              </button>
              {openPortal.isError && (
                <span className="text-xs text-nb-text-3">
                  {t("admin.billing.managePortalError")}
                </span>
              )}
            </div>
          </PermissionGate>
        )}
      </section>

      {/* ---------- Your invoices (Phase 3-invoicing) ---------- */}
      {invoiceRows.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("admin.billing.invoicesTitle")}
          </h2>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-nb-text-3">
              <tr>
                <th className="py-1">{t("admin.billing.invoiceNumber", "Invoice #")}</th>
                <th className="py-1">{t("admin.billing.invoicePeriod")}</th>
                <th className="py-1 text-right">{t("admin.billing.invoiceTotal")}</th>
                <th className="py-1">{t("admin.billing.invoiceStatusHdr")}</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {invoiceRows.map((inv) => {
                const label =
                  inv.status === "paid"
                    ? t("admin.billing.invStatusPaid")
                    : inv.status === "void" || inv.status === "uncollectible"
                      ? t("admin.billing.invStatusVoid")
                      : t("admin.billing.invStatusUnpaid");
                const tone =
                  inv.status === "paid"
                    ? "bg-nb-mint/15 text-nb-mint"
                    : inv.status === "void" || inv.status === "uncollectible"
                      ? "bg-slate-700/40 text-nb-text-3"
                      : "bg-amber-500/15 text-amber-300";
                return (
                  <tr key={inv.id} className="border-t border-nb-border">
                    <td className="py-1.5 font-mono text-xs text-nb-text">
                      {inv.number || "—"}
                    </td>
                    <td className="py-1.5 text-nb-text-2">
                      {formatDate(inv.period_start)}
                    </td>
                    <td className="py-1.5 text-right text-nb-text">
                      €{inv.total_eur}
                    </td>
                    <td className="py-1.5">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] ${tone}`}>
                        {label}
                      </span>
                    </td>
                    <td className="py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        {inv.status !== "paid" && inv.hosted_url && (
                          <a
                            href={inv.hosted_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary text-xs"
                          >
                            {t("admin.billing.invoicePay")}
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => void openInvoicePdf(inv)}
                        >
                          {t("admin.billing.invoicePdf", "PDF")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={invoicePage}
            pageSize={INVOICES_PAGE_SIZE}
            count={invoiceTotal}
            onPage={setInvoicePage}
            disabled={invoices.isFetching}
          />
        </section>
      )}

      {/* ---------- Plan switcher (managed accounts get a request flow) ---------- */}
      {usage.is_managed ? (
        <PermissionGate perm="billing.read">
          <ManagedAccountPanel
            sessionPrice={usage.effective_session_price_eur}
            onRequest={(msg) => requestPlanChange.mutate(msg)}
            pending={requestPlanChange.isPending}
            done={requestPlanChange.isSuccess}
          />
        </PermissionGate>
      ) : (
      <PermissionGate perm="billing.update">
        <section className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("admin.billing.changePlan")}
          </h2>

          {/* Unlimited-operators note + annual toggle, shared across cards. */}
          <div className="flex flex-wrap items-center gap-4 rounded-md bg-slate-900/40 p-3 text-sm">
            <span className="text-nb-text-2">
              {t("admin.billing.unlimitedOperatorsNote")}
            </span>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={annualBilling}
                onChange={(e) => setAnnualBilling(e.target.checked)}
              />
              <span className="text-nb-text-2">
                {t("admin.billing.annualCheckbox")}
              </span>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {plans.data
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((plan) => {
                const isCurrent = plan.slug === currentSlug;
                return (
                  <PlanCard
                    key={plan.slug}
                    plan={plan}
                    isCurrent={isCurrent}
                    annualBilling={annualBilling}
                    onSubscribe={() => subscribe.mutate(plan.slug)}
                    submitPending={subscribe.isPending}
                  />
                );
              })}
          </div>
          {subscribe.isError && (
            <p className="text-xs text-cvp-danger">
              {(subscribe.error as Error).message ?? t("admin.billing.switchError")}
            </p>
          )}
        </section>
      </PermissionGate>
      )}

      {/* ---------- Request a signature pack (managed orgs) ---------- */}
      {/* Managed orgs can't self-checkout, so they request from their
          reseller, who grants it. */}
      {usage.is_managed && (
        <PermissionGate perm="billing.update">
          <section className="card space-y-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                {t("admin.billing.requestPackTitle")}
              </h2>
              <p className="text-xs text-nb-text-3">
                {t("admin.billing.requestPackLead")}
              </p>
            </div>
            <SignaturePackFields
              value={packForm}
              onChange={(patch) => setPackForm((f) => ({ ...f, ...patch }))}
            />
            <div>
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={requestPack.isPending || packForm.quantity < 1}
                onClick={() => requestPack.mutate(packForm)}
              >
                {t("admin.billing.requestPackBtn")}
              </button>
            </div>
            {!!packRequests.data?.length && (
              <ul className="divide-y divide-nb-border text-sm">
                {packRequests.data.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between py-1.5"
                  >
                    <span className="text-nb-text-2">
                      {r.quantity}× {r.level.toUpperCase()}
                      {r.setups > 0 ? ` + ${r.setups} setups` : ""}
                    </span>
                    <span
                      className={
                        "rounded px-1.5 py-0.5 text-[10px] " +
                        (r.status === "granted"
                          ? "bg-nb-mint/15 text-nb-mint"
                          : r.status === "declined"
                            ? "bg-slate-700/40 text-nb-text-3"
                            : "bg-amber-500/15 text-amber-300")
                      }
                    >
                      {t(`admin.billing.requestStatus_${r.status}`)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </PermissionGate>
      )}

      {/* ---------- Buy pre-paid signature packs ---------- */}
      {/* Self-serve only: managed orgs request from their reseller instead
          (above); direct orgs self-checkout via Stripe. */}
      {!usage.is_managed && packs.data && packs.data.length > 0 && (
        <PermissionGate perm="billing.update">
          <section className="card space-y-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                {t("admin.billing.packsTitle")}
              </h2>
              <p className="text-xs text-nb-text-3">
                {t("admin.billing.packsLead")}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {packs.data.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center justify-between gap-3 rounded-md border border-nb-border p-3"
                >
                  <div>
                    <div className="text-sm font-medium text-nb-text">
                      {p.label}
                    </div>
                    <div className="text-xs text-nb-text-3">
                      {p.level.toUpperCase()} · €{p.price_eur}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={!p.purchasable || buyPack.isPending}
                    onClick={() => buyPack.mutate(p.key)}
                    title={
                      !p.purchasable
                        ? t("admin.billing.packUnavailable")
                        : undefined
                    }
                  >
                    {buyPack.isPending
                      ? t("common.loading")
                      : t("admin.billing.packBuy")}
                  </button>
                </div>
              ))}
            </div>
            {buyPack.isError && (
              <p className="text-xs text-cvp-danger">
                {t("admin.billing.packBuyError")}
              </p>
            )}
          </section>
        </PermissionGate>
      )}
    </div>
  );
}


/** Managed (reseller/commercial) accounts: no self-serve plan grid + no
 *  public list price. Show their own session price + a request flow
 *  routed to their account manager / ops (§9a). */
function ManagedAccountPanel({
  sessionPrice,
  onRequest,
  pending,
  done,
}: {
  sessionPrice: string;
  onRequest: (message: string) => void;
  pending: boolean;
  done: boolean;
}) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  return (
    <section className="card space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
        {t("admin.billing.managedTitle")}
      </h2>
      <p className="text-xs text-nb-text-3">
        {t("admin.billing.managedBlurb")}
      </p>
      <div className="rounded-md bg-slate-900/40 p-3 text-sm text-nb-text">
        {t("admin.billing.managedYourSessionPrice", { price: sessionPrice })}
      </div>
      <textarea
        className="input w-full text-sm"
        rows={3}
        placeholder={t("admin.billing.managedRequestPlaceholder")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={() => onRequest(message)}
          disabled={pending || done}
        >
          {pending
            ? t("admin.billing.managedRequesting")
            : t("admin.billing.managedRequestButton")}
        </button>
        {done && (
          <span className="text-xs text-nb-mint">
            {t("admin.billing.managedRequestSent")}
          </span>
        )}
      </div>
    </section>
  );
}


/** Pricing line under the plan name. Shows per-axis prices in a
 *  compact form so the customer can see what's billable. */
function PlanPricingLine({ plan }: { plan: Plan }) {
  const { t } = useTranslation();
  const base = Number(plan.base_price_eur);
  const conf = Number(plan.price_per_conference_eur);
  if (base === 0 && conf === 0) {
    return (
      <p className="text-xs text-nb-text-3">
        {plan.trial_conferences > 0
          ? t("admin.billing.trialConferencesSummary", {
              count: plan.trial_conferences,
            })
          : plan.description || t("admin.billing.noPricingPlaceholder")}
      </p>
    );
  }
  return (
    <p className="text-xs text-nb-text-3">
      {t("admin.billing.pricingLineMain", {
        base: plan.base_price_eur,
        conf: plan.price_per_conference_eur,
      })}
      {Number(plan.price_signature_l3_eur) > 0 &&
        t("admin.billing.pricingLineQesSuffix", {
          qes: plan.price_signature_l3_eur,
        })}
    </p>
  );
}


/** Per-axis usage breakdown — replaces the old "minutes consumed"
 *  progress bar for the new pricing axes. Trial-state callout
 *  surfaces when the org is on a trial-bounded plan. */
function UsageGrid({ usage }: { usage: BillingMeResponse["usage"] }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-xs text-nb-text-3">
        <span>{t("admin.billing.usagePeriodLabel")}</span>
        <span>
          {formatDate(usage.period_start)} →{" "}
          {formatDate(usage.period_end)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <UsageTile
          label={t("admin.billing.usageBaseFee")}
          value={`€${usage.base_cost_eur}`}
          cost={t("admin.billing.unlimitedOperatorsShort")}
        />
        <UsageTile
          label={t("admin.billing.usageConferences")}
          value={String(usage.sessions_count)}
          cost={`€${usage.sessions_cost_eur}`}
          // A call covering four sites is billed as four inspections but
          // counts as ONE conference, so without this the cost simply
          // doesn't divide by the number above.
          subValues={[
            (usage.extra_site_units ?? 0) > 0 &&
              t("admin.billing.usageExtraSites", {
                count: usage.extra_site_units,
              }),
          ].filter(Boolean) as string[]}
        />
        <UsageTile
          label={t("admin.billing.usageSignatures")}
          value={String(
            usage.signatures_l1_count +
              usage.signatures_l2_count +
              usage.signatures_l3_count,
          )}
          cost={`€${usage.signatures_cost_eur}`}
          subValues={[
            usage.signatures_l1_count > 0 &&
              t("admin.billing.usageL1", { count: usage.signatures_l1_count }),
            usage.signatures_l2_count > 0 &&
              t("admin.billing.usageL2", { count: usage.signatures_l2_count }),
            usage.signatures_l3_count > 0 &&
              t("admin.billing.usageL3", { count: usage.signatures_l3_count }),
            // "used" is the count above; also surface pre-paid credits still
            // AVAILABLE so a fresh pack doesn't look like "0 signatures".
            usage.signature_pack_credits.l1 +
              usage.signature_pack_credits.l2 +
              usage.signature_pack_credits.l3 >
              0 &&
              t("admin.billing.usageCreditsAvailable", {
                count:
                  usage.signature_pack_credits.l1 +
                  usage.signature_pack_credits.l2 +
                  usage.signature_pack_credits.l3,
              }),
          ].filter(Boolean) as string[]}
        />
        <UsageTile
          label={t("admin.billing.usageProjectedTotal")}
          value={`€${usage.projected_total_eur}`}
          highlight
        />
      </div>

      {usage.trial_conferences_total > 0 && (
        <div className="mt-2 rounded-md bg-nb-mint/10 p-2 text-xs text-nb-text ring-1 ring-nb-mint/30">
          {t("admin.billing.trialRemaining", {
            remaining: usage.trial_conferences_remaining,
            total: usage.trial_conferences_total,
          })}{" "}
          {usage.trial_conferences_remaining === 0 && (
            <span className="text-cvp-warn">
              {t("admin.billing.trialUpgrade")}
            </span>
          )}
        </div>
      )}

      {(usage.signature_pack_credits.l1 > 0 ||
        usage.signature_pack_credits.l2 > 0 ||
        usage.signature_pack_credits.l3 > 0) && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-200">
          <span className="mr-1 font-semibold">✓</span>
          {t("admin.billing.packCredits", {
            l1: usage.signature_pack_credits.l1,
            l2: usage.signature_pack_credits.l2,
            l3: usage.signature_pack_credits.l3,
          })}
        </div>
      )}
    </div>
  );
}


function UsageTile({
  label,
  value,
  cost,
  subValues,
  highlight,
}: {
  label: string;
  value: string;
  cost?: string;
  subValues?: string[];
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-md border p-2 text-xs ${
        highlight ? "border-nb-mint bg-nb-mint/5" : "border-nb-border bg-slate-900/40"
      }`}
    >
      <div className="text-nb-text-3">{label}</div>
      <div className="text-base font-semibold text-nb-text">{value}</div>
      {cost && <div className="text-nb-text-2">{cost}</div>}
      {subValues && subValues.length > 0 && (
        <div className="mt-1 space-x-2 text-[10px] text-nb-text-3">
          {subValues.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}


function PlanCard({
  plan,
  isCurrent,
  annualBilling,
  onSubscribe,
  submitPending,
}: {
  plan: Plan;
  isCurrent: boolean;
  annualBilling: boolean;
  onSubscribe: () => void;
  submitPending: boolean;
}) {
  const { t } = useTranslation();
  // Plan-card price preview: a flat per-company monthly fee (unlimited
  // operators) + the billing cycle. No seat math anymore.
  const basePrice = Number(plan.base_price_eur);
  const annualPct = plan.annual_discount_pct || 15;
  const effectiveMonthly = annualBilling
    ? basePrice * (1 - annualPct / 100)
    : basePrice;

  return (
    <div
      className={`rounded-md border p-3 ${
        isCurrent ? "border-nb-mint" : "border-nb-border"
      }`}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-semibold text-nb-text">{plan.name}</h3>
          <p className="text-xs text-nb-text-3">{plan.description}</p>
        </div>
        {isCurrent && (
          <span className="text-xs text-nb-mint">
            {t("admin.billing.currentBadge")}
          </span>
        )}
      </div>

      <ul className="mt-2 space-y-1 text-xs text-nb-text-2">
        {basePrice > 0 && (
          <li>
            {t("admin.billing.flatBaseLine", {
              price: plan.base_price_eur,
            })}
          </li>
        )}
        {basePrice > 0 && (
          <li className="text-nb-mint">
            {t("admin.billing.unlimitedOperatorsLine")}
          </li>
        )}
        {Number(plan.price_per_conference_eur) > 0 && (
          <li>
            {t("admin.billing.perConferenceLine", {
              price: plan.price_per_conference_eur,
              minutes: plan.conference_cap_minutes,
            })}
          </li>
        )}
        {plan.trial_conferences > 0 && (
          <li className="text-nb-mint">
            {t("admin.billing.trialConferencesLine", {
              count: plan.trial_conferences,
            })}
          </li>
        )}
        {Number(plan.price_signature_l3_eur) > 0 && (
          <li>
            {t("admin.billing.signaturesLine", {
              l1: plan.price_signature_l1_eur,
              l2: plan.price_signature_l2_eur,
              l3: plan.price_signature_l3_eur,
            })}
          </li>
        )}
      </ul>

      {basePrice > 0 && (
        <p className="mt-2 text-xs text-nb-text">
          <strong className="text-nb-text">
            {t("admin.billing.monthlyCost", {
              amount: effectiveMonthly.toFixed(2),
            })}
          </strong>
          {annualBilling && (
            <span className="text-nb-mint">
              {t("admin.billing.annualSuffix", { pct: annualPct })}
            </span>
          )}
          {t("admin.billing.plusUsageSuffix")}
        </p>
      )}

      <div className="mt-2">
        {!plan.is_self_serve ? (
          <a
            href="mailto:sales@nexbasira.com"
            className="btn-ghost text-xs"
          >
            {t("admin.billing.contactSales")}
          </a>
        ) : isCurrent ? (
          <button
            type="button"
            className="btn-ghost text-xs cursor-default"
            disabled
          >
            {t("admin.billing.currentPlanButton")}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => {
              const isDowngrade = plan.slug === "free";
              const msg = isDowngrade
                ? t("admin.billing.downgradeConfirm")
                : t("admin.billing.subscribeConfirmFlat", {
                    name: plan.name,
                    amount: effectiveMonthly.toFixed(2),
                  });
              if (confirm(msg)) {
                onSubscribe();
              }
            }}
            disabled={submitPending}
          >
            {submitPending
              ? t("admin.billing.subscribing")
              : plan.slug === "free"
                ? t("admin.billing.downgradeButton")
                : t("admin.billing.subscribeButton")}
          </button>
        )}
      </div>
    </div>
  );
}
